import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { CookieBanner } from "@/components/CookieBanner";
import { DynamicBranding } from "@/components/DynamicBranding";
import { ClientProvider } from "@/contexts/ClientContext";
import { ConsentProvider } from "@/contexts/ConsentContext";
import BottomNav from "./components/BottomNav";
import PlayerAuthGuard from "./components/PlayerAuthGuard";
import { SuperAdminGuard } from "./components/SuperAdminGuard";
import { TenantAdminGuard } from "./components/TenantAdminGuard";
import { PlayerAccessGuard } from "./components/PlayerAccessGuard";
import { OrganizationManagerGuard } from "./components/OrganizationManagerGuard";

// Eagerly loaded - landing page
import Index from "./pages/Index";

// Lazy loaded pages
const GameGateRedirect = lazy(() => import("./pages/GameGateRedirect"));
const WheelGame = lazy(() => import("./pages/WheelGame"));
const BlackjackGame = lazy(() => import("./pages/BlackjackGame"));
const SlotMachineGame = lazy(() => import("./pages/SlotMachineGame"));
const ResultPage = lazy(() => import("./pages/ResultPage"));

const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminSettings = lazy(() => import("./pages/AdminSettings"));
const AdminActionBlocks = lazy(() => import("./pages/AdminActionBlocks"));
const AdminEmailLogs = lazy(() => import("./pages/AdminEmailLogs"));
const AdminNewsletter = lazy(() => import("./pages/AdminNewsletter"));
const AdminAuditLogs = lazy(() => import("./pages/AdminAuditLogs"));
const NewsletterRedeem = lazy(() => import("./pages/NewsletterRedeem"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const NewsletterUnsubscribe = lazy(() => import("./pages/NewsletterUnsubscribe"));
const Auth = lazy(() => import("./pages/Auth"));
const SetPassword = lazy(() => import("./pages/SetPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const NotFound = lazy(() => import("./pages/NotFound"));
const TicketView = lazy(() => import("./pages/TicketView"));
const ShortLinkRedirect = lazy(() => import("./pages/ShortLinkRedirect"));
const Vouchers = lazy(() => import("./pages/Vouchers"));
const Menu = lazy(() => import("./pages/Menu"));
const PlayerAuth = lazy(() => import("./pages/PlayerAuth"));
const ActionHistory = lazy(() => import("./pages/ActionHistory"));
const SuperAdminDashboard = lazy(() => import("./pages/SuperAdminDashboard"));
const SuperAdminClientDetail = lazy(() => import("./pages/SuperAdminClientDetail"));
const SuperAdminTools = lazy(() => import("./pages/SuperAdminTools"));
const SuperAdminCRM = lazy(() => import("./pages/SuperAdminCRM"));
const SuperAdminTesterDevices = lazy(() => import("./pages/SuperAdminTesterDevices"));
const PmProjectsHub = lazy(() => import("./pages/superadmin/pm/ProjectsHub"));
const PmProjectShellPage = lazy(() => import("./pages/superadmin/pm/ProjectShellPage"));
const PmProjectBoard = lazy(() => import("./pages/superadmin/pm/ProjectBoard"));
const PmProjectList = lazy(() => import("./pages/superadmin/pm/ProjectList"));
const PmProjectCalendar = lazy(() => import("./pages/superadmin/pm/ProjectCalendarPage"));
const PmProjectTimeline = lazy(() => import("./pages/superadmin/pm/ProjectTimeline"));
const PmProjectSprints = lazy(() => import("./pages/superadmin/pm/ProjectSprints"));
const PmProjectSettings = lazy(() => import("./pages/superadmin/pm/ProjectSettings"));
const PmGlobalCalendar = lazy(() => import("./pages/superadmin/pm/GlobalCalendar"));
const PmInbox = lazy(() => import("./pages/superadmin/pm/Inbox"));
const PmWikiHub = lazy(() => import("./pages/superadmin/pm/WikiHub"));
const PmWikiSpace = lazy(() => import("./pages/superadmin/pm/WikiSpace"));
const PmWikiPage = lazy(() => import("./pages/superadmin/pm/WikiPage"));
const OrganizationDashboard = lazy(() => import("./pages/OrganizationDashboard"));

const Signup = lazy(() => import("./pages/Signup"));
const Checkout = lazy(() => import("./pages/Checkout"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-background">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const queryClient = new QueryClient();

const AppContent = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin') || location.pathname === '/auth';
  const isClientRoute = location.pathname.startsWith('/c/');
  const isAdminOrSuperAdminRoute = location.pathname.includes('/admin') || location.pathname.includes('/superadmin');
  // Player-facing surfaces (landing, games, coupons, profile, legal) get the tenant design voice.
  const isPlayerRoute = isClientRoute && !isAdminOrSuperAdminRoute;

  // Corporate (Feednback brand) routes: marketing/auth pages + the "default" client slug.
  // Tenants with their own branding override via DynamicBranding at runtime; admin/superadmin
  // routes always keep the Neon Night theme defined on :root.
  const path = location.pathname;
  const isCorporateRoute =
    !isAdminOrSuperAdminRoute &&
    !path.startsWith('/organization') &&
    (
      path === '/' ||
      path === '/signup' ||
      path === '/checkout' ||
      path === '/payment-success' ||
      path === '/auth' ||
      path === '/auth/reset-password' ||
      path === '/set-password' ||
      path === '/c/default' ||
      path.startsWith('/c/default/')
    );

  return (
    <main className={[isCorporateRoute ? 'corporate-theme' : '', isPlayerRoute ? 'player-theme' : ''].join(' ').trim()}>
      <DynamicBranding />
      <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Redirect root to default client */}
        <Route path="/" element={<Navigate to="/c/default" replace />} />
        
        {/* Auth routes (no client context) */}
        <Route path="/auth" element={<Auth />} />
        <Route path="/auth/reset-password" element={<ResetPassword />} />
        <Route path="/set-password" element={<SetPassword />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/payment-success" element={<PaymentSuccess />} />
        
        {/* Ticket view route (accessible from email links) */}
        <Route path="/ticket/:ticketId" element={<TicketView />} />
        <Route path="/s/:slug" element={<ShortLinkRedirect />} />
        
        {/* Super admin routes (protected) */}
        <Route path="/superadmin/dashboard" element={
          <SuperAdminGuard>
            <SuperAdminDashboard />
          </SuperAdminGuard>
        } />
        <Route path="/superadmin/clients/:clientId" element={
          <SuperAdminGuard>
            <SuperAdminClientDetail />
          </SuperAdminGuard>
        } />
        <Route path="/superadmin/tools" element={
          <SuperAdminGuard>
            <SuperAdminTools />
          </SuperAdminGuard>
        } />
        <Route path="/superadmin/crm" element={
          <SuperAdminGuard>
            <SuperAdminCRM />
          </SuperAdminGuard>
        } />
        <Route path="/superadmin/tester-devices" element={
          <SuperAdminGuard>
            <SuperAdminTesterDevices />
          </SuperAdminGuard>
        } />

        {/* Project Management module */}
        <Route path="/superadmin/projects" element={
          <SuperAdminGuard>
            <PmProjectsHub />
          </SuperAdminGuard>
        } />
        <Route path="/superadmin/projects/:projectKey" element={
          <SuperAdminGuard>
            <PmProjectShellPage />
          </SuperAdminGuard>
        }>
          <Route index element={<Navigate to="board" replace />} />
          <Route path="board" element={<PmProjectBoard />} />
          <Route path="list" element={<PmProjectList />} />
          <Route path="calendar" element={<PmProjectCalendar />} />
          <Route path="timeline" element={<PmProjectTimeline />} />
          <Route path="sprints" element={<PmProjectSprints />} />
          <Route path="settings" element={<PmProjectSettings />} />
        </Route>
        <Route path="/superadmin/calendar" element={
          <SuperAdminGuard>
            <PmGlobalCalendar />
          </SuperAdminGuard>
        } />
        <Route path="/superadmin/inbox" element={
          <SuperAdminGuard>
            <PmInbox />
          </SuperAdminGuard>
        } />
        <Route path="/superadmin/wiki" element={
          <SuperAdminGuard>
            <PmWikiHub />
          </SuperAdminGuard>
        } />
        <Route path="/superadmin/wiki/:spaceSlug" element={
          <SuperAdminGuard>
            <PmWikiSpace />
          </SuperAdminGuard>
        } />
        <Route path="/superadmin/wiki/:spaceSlug/:pageSlug" element={
          <SuperAdminGuard>
            <PmWikiPage />
          </SuperAdminGuard>
        } />

        {/* Organization manager routes (protected) */}
        <Route path="/organization" element={
          <OrganizationManagerGuard>
            <OrganizationDashboard />
          </OrganizationManagerGuard>
        } />
        <Route path="/organization/:organizationId" element={
          <OrganizationManagerGuard>
            <OrganizationDashboard />
          </OrganizationManagerGuard>
        } />
        
        {/* Client-scoped routes */}
        <Route path="/c/:clientSlug/*" element={
          <>
            <Routes>
              {/* Player-facing routes: gated by PlayerAccessGuard so the
                  landing, games, vouchers and menu are frozen as soon as
                  the tenant status is not in ('trialing','active'). */}
              <Route path="/" element={<PlayerAccessGuard><Index /></PlayerAccessGuard>} />
              <Route path="/player-auth" element={<PlayerAuth />} />
              {/* Redirect old /game/:type to /play/:type */}
              <Route path="/game/:gameType" element={<PlayerAccessGuard><GameGateRedirect /></PlayerAccessGuard>} />
              <Route path="/play/wheel" element={<PlayerAccessGuard><WheelGame /></PlayerAccessGuard>} />
              <Route path="/play/blackjack" element={<PlayerAccessGuard><BlackjackGame /></PlayerAccessGuard>} />
              <Route path="/play/slot" element={<PlayerAccessGuard><SlotMachineGame /></PlayerAccessGuard>} />
              <Route path="/result" element={<PlayerAccessGuard><ResultPage /></PlayerAccessGuard>} />

              {/* Public newsletter routes */}
              <Route path="/newsletter/:token" element={<NewsletterRedeem />} />
              <Route path="/newsletter/unsubscribe/:token" element={<NewsletterUnsubscribe />} />

              {/* Public legal pages (RGPD) */}
              <Route path="/legal/mentions" element={<LegalPage kind="mentions" />} />
              <Route path="/legal/confidentialite" element={<LegalPage kind="confidentialite" />} />
              <Route path="/legal/reglement" element={<LegalPage kind="reglement" />} />

              {/* Protected player routes.
                  Vouchers stays accessible even when the tenant is frozen so
                  a customer can still redeem a coupon they have already won.
                  Menu is part of the player experience and is gated. */}
              <Route path="/vouchers" element={<PlayerAuthGuard><Vouchers /></PlayerAuthGuard>} />
              <Route path="/menu" element={<PlayerAccessGuard><PlayerAuthGuard><Menu /></PlayerAuthGuard></PlayerAccessGuard>} />
              <Route path="/menu/history" element={<PlayerAuthGuard><ActionHistory /></PlayerAuthGuard>} />

              {/* Admin routes (protected by tenant guard, not by player freeze) */}
              <Route path="/admin" element={
                <TenantAdminGuard>
                  <AdminDashboard />
                </TenantAdminGuard>
              } />
              <Route path="/admin/settings" element={
                <TenantAdminGuard>
                  <AdminSettings />
                </TenantAdminGuard>
              } />
              <Route path="/admin/action-blocks" element={
                <TenantAdminGuard>
                  <AdminActionBlocks />
                </TenantAdminGuard>
              } />
              <Route path="/admin/emails" element={
                <TenantAdminGuard>
                  <AdminEmailLogs />
                </TenantAdminGuard>
              } />
              <Route path="/admin/newsletter" element={
                <TenantAdminGuard>
                  <AdminNewsletter />
                </TenantAdminGuard>
              } />
              <Route path="/admin/historique" element={
                <TenantAdminGuard>
                  <AdminAuditLogs />
                </TenantAdminGuard>
              } />
              <Route path="/superadmin/historique" element={
                <SuperAdminGuard>
                  <AdminAuditLogs />
                </SuperAdminGuard>
              } />
            </Routes>
          </>
        } />
        
        {/* Catch-all */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
      {isClientRoute && !isAdminOrSuperAdminRoute && !location.pathname.includes('/play/') && <BottomNav />}
      {!isAdminRoute && !isClientRoute && <CookieBanner />}
    </main>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ClientProvider>
          <ConsentProvider>
            <AppContent />
          </ConsentProvider>
        </ClientProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
